import React, { useState, useEffect } from 'react';
import { formatDate } from '../lib/dateUtils';
import { 
  Rocket, 
  Zap, 
  Activity, 
  Globe, 
  ShieldCheck, 
  Send, 
  Loader2, 
  Share2, 
  ExternalLink,
  Copy,
  Check,
  Megaphone,
  Terminal,
  Sparkles,
  Link as LinkIcon,
  ArrowLeft,
  Folder,
  ChevronDown,
  ChevronRight,
  Monitor,
  X,
  Layers,
  Github,
  Plus,
  GitBranch,
  History,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { GoogleGenAI } from "@google/genai";
import { getGeminiKey, chatWithGemini } from '../lib/gemini';
import { useSearchParams, Link } from 'react-router-dom';
import { db, auth, collection, query, where, onSnapshot, addDoc, serverTimestamp, doc, updateDoc, getDocs, limit, orderBy, deleteDoc } from '../firebase';
import { toast, Toaster } from 'sonner';
import { trackEvent } from '../lib/analytics';
import { useTasks } from '../contexts/TaskContext';

const GitHubIntegration = ({ projectId, profile, project }: { projectId: string, profile: any, project?: any }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [installations, setInstallations] = useState<any[]>([]);
  const [selectedInstId, setSelectedInstId] = useState<string>('');
  const [repos, setRepos] = useState<any[]>([]);
  const [selectedRepo, setSelectedRepo] = useState<any>(null);
  const [connection, setConnection] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isPushing, setIsPushing] = useState(false);
  const [mode, setMode] = useState<'create' | 'link' | 'none'>('none');
  const [newRepoName, setNewRepoName] = useState('');
  const [isPrivate, setIsPrivate] = useState(true);
  const [commitMessage, setCommitMessage] = useState('');
  const [branchMode, setBranchMode] = useState<'default' | 'new-branch'>('default');
  const [newBranchName, setNewBranchName] = useState('');

  useEffect(() => {
    if (!projectId) return;
    const q = query(
      collection(db, 'github_repo_connections'),
      where('projectId', '==', projectId),
      limit(1)
    );
    return onSnapshot(q, (snap) => {
      if (!snap.empty) {
        setConnection(snap.docs[0].data());
      } else {
        setConnection(null);
      }
    });
  }, [projectId]);

  useEffect(() => {
    if (installations.length > 0 && !selectedInstId) {
      if (installations.length === 1) {
        setSelectedInstId(installations[0].githubInstallationId.toString());
      } else if (project?.githubInstallationId) {
        const matching = installations.find(i => i.githubInstallationId.toString() === project.githubInstallationId.toString());
        if (matching) {
          setSelectedInstId(matching.githubInstallationId.toString());
        }
      }
    }
  }, [installations, project?.githubInstallationId]);

  const fetchInstallations = async () => {
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        console.warn('[GitHub] Not fetching installations: No auth token');
        return;
      }
      
      console.log('[GitHub] Fetching installations from API...');
      const res = await fetch('/api/github/installations', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (Array.isArray(data)) {
        console.log(`[GitHub] Successfully loaded ${data.length} installations`);
        setInstallations(data);
      } else {
        console.error('[GitHub] Installations data is not an array:', data);
        setInstallations([]);
      }
    } catch (error) {
      console.error('[GitHub] Failed to fetch installations', error);
      setInstallations([]);
    }
  };

  useEffect(() => {
    if (auth.currentUser) {
      fetchInstallations();
    }
  }, [auth.currentUser]);

  useEffect(() => {
    // If we land back from a non-popup flow with success params, refresh and ensure link.
    const success = searchParams.get('github_setup');
    const instId = searchParams.get('installation_id');
    
    if (success === 'success' && instId) {
      console.log('[GitHub] Found success param and installation_id in URL, ensuring sync...', instId);
      handleLinkInstallation(instId);
      
      // Clean up URL params
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('github_setup');
      newParams.delete('installation_id');
      setSearchParams(newParams, { replace: true });
    } else if (success === 'success') {
      console.log('[GitHub] Found success param in URL, refreshing installations');
      fetchInstallations();
      
      // Clean up URL params
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('github_setup');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, auth.currentUser]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Validate origin - be more lenient in dev but still safe
      const isAllowedOrigin = 
        event.origin === window.location.origin ||
        event.origin.endsWith('.run.app') || 
        event.origin.includes('localhost') || 
        event.origin.includes('127.0.0.1');

      if (!isAllowedOrigin) {
        console.warn('[GitHub] Ignored message from untrusted origin:', event.origin);
        return;
      }

      console.log('[GitHub] Message received in parent', { origin: event.origin, data: event.data });

      if (event.data?.type === 'GITHUB_INSTALL_SUCCESS') {
        const instId = event.data.installation_id;
        console.log('[GitHub] Installation successful from popup, linking...', instId);
        handleLinkInstallation(instId);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const handleLinkInstallation = async (githubInstallationId: string) => {
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) return;
      const res = await fetch('/api/github/installations/link', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ githubInstallationId })
      });
      const data = await res.json();
      if (data.success) {
        toast.success("GitHub Account connected successfully");
        fetchInstallations();
      }
    } catch (error) {
      console.error("Link failed", error);
    }
  };

  const handleConnect = () => {
    // Generate the install URL as per Part 1
    const githubAppName = "yacht-labs"; // This can be environment-specific if needed
    const url = `https://github.com/apps/${githubAppName}/installations/new?state=${projectId}`;
    console.log('[GitHub] Navigating to GitHub App installation', { url, projectId });
    window.location.href = url;
  };

  useEffect(() => {
    if (selectedInstId && selectedInstId !== 'new') {
      const fetchRepos = async () => {
        setIsLoading(true);
        try {
          const token = await auth.currentUser?.getIdToken();
          const res = await fetch(`/api/github/installations/${selectedInstId}/repos`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const data = await res.json();
          if (Array.isArray(data)) {
            setRepos(data);
          } else {
            console.error('Repos data is not an array:', data);
            setRepos([]);
          }
        } catch (error) {
          console.error('Failed to fetch repos', error);
          setRepos([]);
        } finally {
          setIsLoading(false);
        }
      };
      fetchRepos();
    } else if (selectedInstId === 'new') {
      handleConnect();
      setSelectedInstId('');
    }
  }, [selectedInstId]);

  const handleCreateRepo = async () => {
    if (!selectedInstId || !newRepoName) return;
    setIsLoading(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch('/api/github/repos/create', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({
          installationId: selectedInstId,
          repoName: newRepoName,
          private: isPrivate,
          description: 'Generated by Yacht Labs'
        })
      });
      const repo = await res.json();
      if (repo.error) throw new Error(repo.error);

      // Link it
      await handleLinkRepo(repo.name, repo.owner.login, repo.full_name, repo.default_branch);
      toast.success("GitHub repository created and linked");
      setMode('none');
    } catch (error: any) {
      toast.error(`Creation failed: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLinkRepo = async (name?: string, owner?: string, fullName?: string, defaultBranch?: string) => {
    const r = selectedRepo || { name, owner, full_name: fullName, default_branch: defaultBranch };
    if (!r) return;
    
    setIsLoading(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch('/api/github/repos/link', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({
          projectId,
          installationId: selectedInstId,
          owner: r.owner,
          repo: r.name,
          fullName: r.full_name,
          defaultBranch: r.default_branch
        })
      });
      await res.json();
      toast.success("GitHub repository linked");
      setMode('none');
    } catch (error) {
      toast.error("Linking failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePush = async () => {
    setIsPushing(true);
    const toastId = toast.loading("Pushing latest build to GitHub...");
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(`/api/github/projects/${projectId}/push`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({
          commitMessage: commitMessage || 'Yacht Labs Export',
          branchMode,
          branchName: newBranchName
        })
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      
      toast.success("Successfully pushed to GitHub", {
        id: toastId,
        description: `Exported ${data.pushedFiles} files to ${data.branch}`
      });
      setCommitMessage('');
      setBranchMode('default');
      setNewBranchName('');
    } catch (error: any) {
      toast.error(`Push failed: ${error.message}`, { id: toastId });
    } finally {
      setIsPushing(false);
    }
  };

  return (
    <div className="tech-panel p-8 space-y-8">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 bg-[#111111] border border-[#222222] rounded-lg flex items-center justify-center text-white">
            <Github size={20} />
          </div>
          <div>
            <h3 className="tech-label text-white text-lg">GitHub Integration</h3>
            <p className="text-[10px] text-[#555555] font-mono uppercase tracking-widest">Connect and export source code</p>
          </div>
        </div>
        
        {connection && (
          <div className="flex items-center gap-2 px-3 py-1 bg-[#2ECC40]/10 border border-[#2ECC40]/20 rounded text-[#2ECC40] text-[10px] font-mono uppercase tracking-widest">
            <CheckCircle2 size={12} />
            Connected
          </div>
        )}
      </div>

      {!connection ? (
        <div className="space-y-6">
          {!project?.githubInstallationId ? (
            <div className="flex flex-col items-center text-center py-12 border border-dashed border-[#222222] rounded-lg space-y-6">
              <Github size={48} className="text-[#222222]" />
              <div className="space-y-2">
                <p className="text-sm text-white">GitHub not connected</p>
                <p className="text-xs text-[#555555]">Connect your project to GitHub to enable deployments</p>
              </div>
              <button 
                onClick={handleConnect}
                className="tech-button inline-flex items-center gap-2 px-8 py-3"
              >
                Connect GitHub <Plus size={16} />
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="bg-[#0A0A0A] border border-[#222222] rounded-lg p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-[#0074D9]/10 border border-[#0074D9]/20 rounded-full flex items-center justify-center text-[#0074D9]">
                      <Zap size={16} />
                    </div>
                    <div>
                      <h4 className="text-xs font-mono uppercase tracking-widest text-[#0074D9]">GitHub Connected</h4>
                      <p className="text-[10px] text-zinc-600">Select or create a repository for this project.</p>
                    </div>
                  </div>
                  <button 
                    onClick={handleConnect}
                    className="text-[10px] font-mono uppercase tracking-widest text-[#555555] hover:text-white"
                  >
                    Change Account
                  </button>
                </div>
                
                <div className="space-y-2">
                  <label className="tech-label text-[#555555]">Select GitHub Account</label>
                  <select 
                    className="tech-input w-full"
                    value={selectedInstId}
                    onChange={(e) => setSelectedInstId(e.target.value)}
                  >
                    <option value="">Choose an account...</option>
                    {Array.isArray(installations) && installations.map(inst => (
                      <option key={inst.githubInstallationId} value={inst.githubInstallationId}>
                        {inst.githubAccountLogin} ({inst.githubAccountType})
                      </option>
                    ))}
                    <option value="new">+ Add Another Account</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button 
                  onClick={() => setMode('create')}
                  className={`p-6 border rounded-lg text-left transition-all ${mode === 'create' ? 'border-[#0074D9] bg-[#001F3F]/10' : 'border-[#222222] hover:border-[#333333] bg-[#050505]'}`}
                >
                  <Plus className="text-[#0074D9] mb-4" size={24} />
                  <h4 className="text-white text-sm mb-1 font-mono uppercase tracking-widest">Create Repo</h4>
                  <p className="text-[10px] text-[#555555]">Spin up a fresh repository for this project</p>
                </button>
                <button 
                  onClick={() => setMode('link')}
                  className={`p-6 border rounded-lg text-left transition-all ${mode === 'link' ? 'border-[#0074D9] bg-[#001F3F]/10' : 'border-[#222222] hover:border-[#333333] bg-[#050505]'}`}
                >
                  <LinkIcon className="text-[#0074D9] mb-4" size={24} />
                  <h4 className="text-white text-sm mb-1 font-mono uppercase tracking-widest">Connect Existing Repo</h4>
                  <p className="text-[10px] text-[#555555]">Push to a repository you already own</p>
                </button>
              </div>

              {mode === 'create' && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="p-6 bg-[#0A0A0A] border border-[#222222] rounded-lg space-y-6">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label className="tech-label text-[#555555]">Repository Name</label>
                      <input 
                        type="text" 
                        value={newRepoName}
                        onChange={(e) => setNewRepoName(e.target.value)}
                        placeholder="project-name"
                        className="tech-input w-full"
                      />
                    </div>
                    <div className="flex items-center gap-6">
                      <label className="flex items-center gap-2 cursor-pointer group">
                        <input 
                          type="radio" 
                          name="privacy" 
                          checked={isPrivate} 
                          onChange={() => setIsPrivate(true)}
                          className="hidden" 
                        />
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all ${isPrivate ? 'border-[#0074D9]' : 'border-[#333333]'}`}>
                          {isPrivate && <div className="w-2 h-2 bg-[#0074D9] rounded-full" />}
                        </div>
                        <span className={`text-[10px] font-mono uppercase tracking-widest ${isPrivate ? 'text-white' : 'text-[#555555]'}`}>Private</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer group">
                        <input 
                          type="radio" 
                          name="privacy" 
                          checked={!isPrivate} 
                          onChange={() => setIsPrivate(false)}
                          className="hidden" 
                        />
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all ${!isPrivate ? 'border-[#0074D9]' : 'border-[#333333]'}`}>
                          {!isPrivate && <div className="w-2 h-2 bg-[#0074D9] rounded-full" />}
                        </div>
                        <span className={`text-[10px] font-mono uppercase tracking-widest ${!isPrivate ? 'text-white' : 'text-[#555555]'}`}>Public</span>
                      </label>
                    </div>
                  </div>
                  <button 
                    onClick={handleCreateRepo}
                    disabled={isLoading || !newRepoName}
                    className="tech-button w-full py-4 text-xs"
                  >
                    {isLoading ? <Loader2 className="animate-spin mx-auto" size={18} /> : 'Create Repository and Connect'}
                  </button>
                </motion.div>
              )}

              {mode === 'link' && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="p-6 bg-[#0A0A0A] border border-[#222222] rounded-lg space-y-6">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label className="tech-label text-[#555555]">Select Repository</label>
                      <select 
                        className="tech-input w-full"
                        value={selectedRepo?.full_name || ''}
                        onChange={(e) => setSelectedRepo(repos.find((r: any) => r.full_name === e.target.value))}
                      >
                        <option value="">Choose a repo...</option>
                        {Array.isArray(repos) && repos.map((r: any) => (
                          <option key={r.full_name} value={r.full_name}>{r.full_name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <button 
                    onClick={() => handleLinkRepo()}
                    disabled={isLoading || !selectedRepo}
                    className="tech-button w-full py-4 text-xs"
                  >
                    {isLoading ? <Loader2 className="animate-spin mx-auto" size={18} /> : 'Connect Existing Repository'}
                  </button>
                </motion.div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-8">
           <div className="grid grid-cols-1 md:grid-cols-2 gap-8 ring-1 ring-[#222222] p-8 rounded-lg bg-[#050505]">
             <div className="space-y-6">
                <div className="space-y-1">
                  <p className="text-[10px] font-mono uppercase tracking-widest text-[#555555]">Connected Repository</p>
                  <a 
                    href={connection.html_url || `https://github.com/${connection.fullName}`}
                    target="_blank"
                    className="text-lg text-white hover:text-[#0074D9] transition-colors flex items-center gap-2"
                  >
                    {connection.fullName} <ExternalLink size={14} />
                  </a>
                </div>
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2">
                    <GitBranch size={14} className="text-[#555555]" />
                    <span className="text-[10px] font-mono uppercase tracking-widest text-[#888888]">{connection.defaultBranch}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <History size={14} className="text-[#555555]" />
                    <span className="text-[10px] font-mono uppercase tracking-widest text-[#888888]">
                      {connection.lastPushAt ? `Last push: ${formatDate({ seconds: new Date(connection.lastPushAt).getTime() / 1000 })}` : 'Not pushed yet'}
                    </span>
                  </div>
                </div>
             </div>
             <div className="flex flex-col justify-center gap-6">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <label className="tech-label text-[#555555]">Commit Message</label>
                    <input 
                      type="text"
                      placeholder="Yacht Labs Export"
                      value={commitMessage}
                      onChange={(e) => setCommitMessage(e.target.value)}
                      className="tech-input w-full"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="tech-label text-[#555555]">Destination Branch</label>
                    <div className="flex gap-4">
                      <button 
                        onClick={() => setBranchMode('default')}
                        className={`flex-1 px-4 py-2 border rounded text-[10px] uppercase font-mono tracking-widest transition-all ${branchMode === 'default' ? 'border-[#0074D9] text-white bg-[#0074D9]/10' : 'border-[#222222] text-[#555555] hover:border-[#333333]'}`}
                      >
                        Default ({connection.defaultBranch})
                      </button>
                      <button 
                        onClick={() => setBranchMode('new-branch')}
                        className={`flex-1 px-4 py-2 border rounded text-[10px] uppercase font-mono tracking-widest transition-all ${branchMode === 'new-branch' ? 'border-[#0074D9] text-white bg-[#0074D9]/10' : 'border-[#222222] text-[#555555] hover:border-[#333333]'}`}
                      >
                        New Branch
                      </button>
                    </div>
                  </div>
                  {branchMode === 'new-branch' && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}>
                      <input 
                        type="text"
                        placeholder="branch-name"
                        value={newBranchName}
                        onChange={(e) => setNewBranchName(e.target.value)}
                        className="tech-input w-full"
                      />
                    </motion.div>
                  )}
                </div>

                <div className="space-y-3">
                  <button 
                    onClick={handlePush}
                    disabled={isPushing || (branchMode === 'new-branch' && !newBranchName)}
                    className="tech-button w-full py-4 text-xs flex items-center justify-center gap-2"
                  >
                    {isPushing ? (
                      <>
                        <Loader2 className="animate-spin" size={18} />
                        Pushing...
                      </>
                    ) : (
                      <>
                        Push Latest Build <Send size={14} />
                      </>
                    )}
                  </button>
                  <button 
                    onClick={() => setConnection(null)}
                    className="w-full text-[10px] font-mono uppercase tracking-widest text-[#555555] hover:text-[#FF4444] transition-colors py-2"
                  >
                      Disconnect Repository
                  </button>
                </div>
             </div>
           </div>

           <div className="bg-[#111111]/50 border border-[#222222] border-dashed rounded p-6 flex items-start gap-4">
              <AlertCircle size={18} className="text-[#0074D9] shrink-0" />
              <div>
                <p className="text-xs text-white mb-1">Auto-Deploy Enabled</p>
                <p className="text-[10px] text-[#555555] leading-relaxed">
                  Every time you push from Yacht Labs, we create a clean commit with your latest generated files. GitHub Actions can be configured to pick up these changes automatically.
                </p>
              </div>
           </div>
        </div>
      )}
    </div>
  );
};

const LaunchKitCard = ({ title, content, icon: Icon }: { title: string, content: string, icon: any }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(content);
    setCopied(true);
    toast.success(`${title} copied`);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="tech-panel p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-[#111111] border border-[#222222] rounded-lg flex items-center justify-center text-[#888888]">
            <Icon size={16} />
          </div>
          <h3 className="tech-label text-white">{title}</h3>
        </div>
        <button 
          onClick={handleCopy}
          className="p-2 hover:bg-[#111111] rounded text-[#555555] hover:text-white transition-all"
        >
          {copied ? <Check size={16} className="text-[#2ECC40]" /> : <Copy size={16} />}
        </button>
      </div>
      <div className="bg-[#050505] rounded p-4 border border-[#222222]">
        <p className="text-xs text-[#CCCCCC] font-sans leading-relaxed whitespace-pre-wrap">{content}</p>
      </div>
    </div>
  );
};

export default function Kinetics({ profile, lastBuild, setLastBuild }: { profile: any, lastBuild: any, setLastBuild: any }) {
  const { tasks, startTask, updateTask, completeTask, failTask } = useTasks();
  const launchKitTask = tasks.launchKit;

  const assembleHtml = (mainFile: any, allFiles: any[]) => {
    if (!mainFile?.name?.toLowerCase().endsWith('.html') && !mainFile?.content?.trim().toLowerCase().startsWith('<!doctype html>')) {
      return mainFile?.content || '';
    }

    let assembed = mainFile?.content || '';

    // Inline CSS
    allFiles.forEach(file => {
      if (file.name.endsWith('.css')) {
        const linkTag = new RegExp(`<link[^>]*href=["'](?:./)?${file.name}["'][^>]*>`, 'gi');
        if (linkTag.test(assembed)) {
          assembed = assembed.replace(linkTag, `<style>${file.content}</style>`);
        } else {
          assembed = assembed.replace('</head>', `<style>${file.content}</style></head>`);
        }
      }
    });

    // Inline JS
    allFiles.forEach(file => {
      if (file.name.endsWith('.js')) {
        // Handle both <script src="..."> and <script src="..." defer> variant
        const scriptTag = new RegExp(`<script[^>]*src=["'](?:./)?${file.name}["'][^>]*>\\s*</script>`, 'gi');
        if (scriptTag.test(assembed)) {
          assembed = assembed.replace(scriptTag, `<script>${file.content}</script>`);
        } else {
          assembed = assembed.replace('</body>', `<script>${file.content}</script></body>`);
        }
      }
    });

    return assembed;
  };

  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('project');

  useEffect(() => {
    // Analytics
    trackEvent('page_view_kinetics', { projectId });
  }, [searchParams]);

  const [launchPackage, setLaunchPackage] = useState<any>(null);
  const [launchKit, setLaunchKit] = useState<any>(null);
  const [project, setProject] = useState<any>(null);
  const isGenerating = launchKitTask.status === 'running';
  const [generationTime, setGenerationTime] = useState(0);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [isSavingWebhook, setIsSavingWebhook] = useState(false);
  const [isTestingWebhook, setIsTestingWebhook] = useState(false);
  const [deployments, setDeployments] = useState<any[]>([]);
  const [selectedDeployment, setSelectedDeployment] = useState<any>(null);
  const [showDeployments, setShowDeployments] = useState(false);
  const [activeFileIndex, setActiveFileIndex] = useState(0);
  const [showSupportChat, setShowSupportChat] = useState(false);
  const [supportMessage, setSupportMessage] = useState('');
  const [isSendingSupport, setIsSendingSupport] = useState(false);

  useEffect(() => {
    if (lastBuild) {
      // Create a virtual deployment from the last build for preview in Ship
      const indexFile = lastBuild.files.find((f: any) => f.name.toLowerCase() === 'index.html') || lastBuild.files[0];
      setSelectedDeployment({
        id: lastBuild.docId || 'last-build-preview',
        title: `Draft: ${lastBuild.title || 'Current Build'}`,
        content: indexFile.content,
        files: lastBuild.files,
        status: 'Draft',
        deployedAt: { seconds: Date.now() / 1000 }
      });
    }
  }, [lastBuild]);

  useEffect(() => {
    if (!projectId) return;

    const q = query(
      collection(db, 'deployments'),
      where('projectId', '==', projectId),
      orderBy('deployedAt', 'desc'),
      limit(20) // Optimization: limit background reads
    );

    const unsubscribe = onSnapshot(q, (snap) => {
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setDeployments(docs);
      if (docs.length > 0 && !selectedDeployment) {
        setSelectedDeployment(docs[0]);
      }
    });

    return unsubscribe;
  }, [projectId]);

  useEffect(() => {
    if (!projectId) return;

    // Fetch the latest Launch Package from Asset Vault (Synthesis)
    const q = query(
      collection(db, `projects/${projectId}/documents`),
      where('type', '==', 'launch_package'),
      orderBy('createdAt', 'desc'),
      limit(1)
    );
    
    const unsubscribe = onSnapshot(q, (snap) => {
      if (!snap.empty) {
        setLaunchPackage(snap.docs[0].data());
      }
    });

    // Fetch project settings (webhook)
    const unsubProject = onSnapshot(doc(db, 'projects', projectId), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setProject(data);
        setWebhookUrl(data.webhookUrl || '');
      }
    });

    return () => {
      unsubscribe();
      unsubProject();
    };
  }, [projectId]);

  useEffect(() => {
    let timer: any;
    if (isGenerating) {
      timer = setInterval(() => {
        setGenerationTime(prev => prev + 1);
      }, 1000);
    } else {
      setGenerationTime(0);
    }
    return () => clearInterval(timer);
  }, [isGenerating]);

  const handleGenerateLaunchKit = async () => {
    if (!launchPackage || isGenerating) return;
    
    startTask('launchKit', { projectId });
    
    const prompt = `You are a viral marketing expert. Based on this product launch package:
    
    ${launchPackage.content}
    
    Generate a "Distribution Kit" with:
    1. **Product Hunt**: A single string containing a punchy Title (max 40 chars), followed by a newline, then a Tagline (max 60 chars), followed by two newlines, then a 3-paragraph Description.
    2. **X (Twitter) Thread**: A single string containing a 5-post thread starting with a hook, explaining the problem, the solution, a demo/visual placeholder, and a CTA.
    3. **LinkedIn Post**: A single string containing a professional yet exciting announcement focused on the "Why" and the "ROI".
    
    FORMAT: Output ONLY a JSON object with keys: productHunt, xThread, linkedIn. The values MUST be strings.`;

    const progressInterval = setInterval(() => {
        updateTask('launchKit', { 
            progress: Math.min(95, tasks.launchKit.progress + 10) 
        });
    }, 1000);

    try {
      const response = await chatWithGemini([
        { role: 'user', content: prompt }
      ], "SYSTEM: Output ONLY valid JSON.");
      
      const kit = JSON.parse(response.replace(/```json|```/g, ''));
      setLaunchKit(kit);
      clearInterval(progressInterval);
      completeTask('launchKit', kit);
      toast.success("Launch Kit Generated");
      trackEvent('launch_kit_generated', { projectId });
    } catch (error) {
      clearInterval(progressInterval);
      console.error("Kit generation failed:", error);
      failTask('launchKit', error instanceof Error ? error.message : 'Unknown error');
      toast.error("Generation Failed");
    }
  };

  const handleSaveWebhook = async () => {
    if (!projectId || isSavingWebhook) return;
    setIsSavingWebhook(true);
    try {
      await updateDoc(doc(db, 'projects', projectId), {
        webhookUrl: webhookUrl.trim(),
        updatedAt: serverTimestamp()
      });
      toast.success("Webhook URL saved");
    } catch (error) {
      toast.error("Failed to save webhook");
    } finally {
      setIsSavingWebhook(false);
    }
  };

  const handleTestWebhook = async () => {
    if (!webhookUrl || isTestingWebhook) return;
    setIsTestingWebhook(true);
    try {
      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event: 'test_launch',
          project: launchPackage?.title || 'Test Project',
          timestamp: new Date().toISOString()
        })
      });
      if (response.ok) toast.success("Webhook received successfully");
      else toast.error("Webhook returned error status");
    } catch (error) {
      toast.error("Webhook connection failed");
    } finally {
      setIsTestingWebhook(false);
    }
  };

  const handleSendSupport = async () => {
    if (!supportMessage.trim() || isSendingSupport) return;
    setIsSendingSupport(true);
    try {
      await addDoc(collection(db, 'support_tickets'), {
        projectId,
        userId: auth.currentUser?.uid,
        userEmail: auth.currentUser?.email,
        message: supportMessage.trim(),
        createdAt: serverTimestamp(),
        status: 'open'
      });
      toast.success("Support ticket sent", {
        description: "Our team will get back to you shortly."
      });
      setSupportMessage('');
      setShowSupportChat(false);
    } catch (error) {
      toast.error("Failed to send ticket");
    } finally {
      setIsSendingSupport(false);
    }
  };

  const handleDeleteDeployment = async (e: React.MouseEvent, deploymentId: string) => {
    e.stopPropagation();
    
    const toastId = toast.loading("Deleting deployment...");
    try {
      await deleteDoc(doc(db, 'deployments', deploymentId));
      if (selectedDeployment?.id === deploymentId) {
        setSelectedDeployment(null);
      }
      toast.success("Deployment purged from mission control", { id: toastId });
    } catch (error) {
      console.error("Delete deployment failed:", error);
      toast.error("Failed to delete deployment", { id: toastId });
    }
  };

  if (!projectId) {
    return (
      <div className="h-full flex items-center justify-center text-[#555555] font-mono uppercase text-xs tracking-widest bg-[#050505]">
        Select a project from the dashboard to begin shipping.
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-[#050505]">
      <Toaster position="top-right" theme="dark" />
      <header className="tech-header">
        <div className="flex items-center gap-6">
          <Link 
            to="/studio"
            className="w-10 h-10 bg-[#111111] border border-[#222222] rounded-lg flex items-center justify-center text-[#888888] hover:text-white hover:bg-[#1A1A1A] transition-all"
          >
            <ArrowLeft size={18} />
          </Link>
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-[#001F3F] border border-[#0074D9]/30 rounded-lg flex items-center justify-center">
              <Rocket className="text-[#0074D9]" size={18} />
            </div>
            <div>
              <h1 className="tech-label text-white">Ship: Distribution Engine</h1>
              <p className="text-[10px] text-[#888888] font-sans italic">Deploy technical builds and automate distribution.</p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="relative">
            <button 
              onClick={() => setShowDeployments(!showDeployments)}
              className="flex items-center gap-3 px-4 py-2 bg-[#111111] border border-[#222222] rounded text-[10px] font-mono uppercase tracking-widest text-white hover:bg-[#1A1A1A] transition-all"
            >
              <Folder size={14} className="text-[#555555]" />
              {selectedDeployment?.title || 'No Deployments'}
              <ChevronDown size={14} className="text-[#555555]" />
            </button>

            <AnimatePresence>
              {showDeployments && (
                <motion.div 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  className="absolute top-full right-0 mt-2 w-72 bg-[#0A0A0A] border border-[#222222] rounded shadow-2xl z-50 overflow-hidden"
                >
                  <div className="p-4 border-b border-[#222222]">
                    <h3 className="tech-label text-[#555555]">Ship Navigator</h3>
                  </div>
                  <div className="max-h-64 overflow-y-auto no-scrollbar">
                    {deployments.length === 0 ? (
                      <div className="p-8 text-center">
                        <p className="text-[10px] font-mono uppercase text-[#555555]">No Builds Found</p>
                      </div>
                    ) : (
                      deployments.map((d) => (
                        <div
                          key={d.id}
                          onClick={() => {
                            setSelectedDeployment(d);
                            setShowDeployments(false);
                          }}
                          className={`w-full flex items-center justify-between p-4 hover:bg-[#111111] transition-all text-left cursor-pointer ${selectedDeployment?.id === d.id ? 'bg-[#111111]' : ''}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-2 h-2 rounded-full ${d.status === 'Live' ? 'bg-[#2ECC40]' : 'bg-[#555555]'}`} />
                            <div>
                              <p className="text-xs font-mono text-white truncate max-w-[160px]">{d.title}</p>
                              <p className="text-[8px] font-mono uppercase text-[#555555]">{formatDate(d.deployedAt)}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button 
                              onClick={(e) => handleDeleteDeployment(e, d.id)}
                              className="p-2 text-[#555555] hover:text-[#FF4444] hover:bg-[#FF4444]/10 rounded transition-all"
                              title="Delete deployment"
                            >
                              <X size={14} />
                            </button>
                            <ChevronRight size={14} className="text-[#555555]" />
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto p-8 md:p-12 space-y-12 no-scrollbar">
        <div className="max-w-6xl mx-auto space-y-12">
          {selectedDeployment && (
            <section className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 overflow-x-auto pb-2 no-scrollbar max-w-[70%]">
                  {(selectedDeployment.files || [{ name: 'index.html', content: selectedDeployment.content }]).map((file: any, idx: number) => (
                    <button
                      key={idx}
                      onClick={() => setActiveFileIndex(idx)}
                      className={`px-4 py-2 rounded text-[10px] font-mono uppercase tracking-widest transition-all whitespace-nowrap ${
                        activeFileIndex === idx 
                          ? 'bg-[#0074D9] text-white shadow-[0_0_20px_rgba(0,116,217,0.3)]' 
                          : 'bg-[#111111] text-[#555555] hover:text-[#888888]'
                      }`}
                    >
                      {file.name}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 px-3 py-1 bg-[#111111] border border-[#222222] rounded">
                    <Monitor size={12} className="text-[#555555]" />
                    <span className="text-[10px] font-mono uppercase text-[#888888]">Desktop View</span>
                  </div>
                  <a 
                    href={selectedDeployment.liveUrl || `/pub/${selectedDeployment.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-[#0074D9] hover:text-[#7FDBFF] transition-colors"
                  >
                    Open Live <ExternalLink size={12} />
                  </a>
                </div>
              </div>
              <div className="w-full bg-white rounded overflow-hidden shadow-2xl border border-[#222222] min-h-[600px] relative z-0 group">
                {(() => {
                  const activeFile = (selectedDeployment.files || [{ name: 'index.html', content: selectedDeployment.content }])[activeFileIndex];
                  const isHtml = activeFile?.name?.toLowerCase().endsWith('.html') || activeFile?.content?.trim().toLowerCase().startsWith('<!doctype html>');
                  
                  if (isHtml) {
                    const allFiles = selectedDeployment.files || [{ name: 'index.html', content: selectedDeployment.content }];
                    return (
                      <iframe 
                        srcDoc={assembleHtml(activeFile, allFiles)}
                        className="w-full h-[600px] border-none relative z-0"
                        title="Deployment Preview"
                        sandbox="allow-scripts allow-forms allow-popups allow-modals allow-pointer-lock allow-same-origin"
                        onLoad={(e) => {
                          const iframe = e.target as HTMLIFrameElement;
                          iframe.contentWindow?.focus();
                        }}
                      />
                    );
                  } else {
                    return (
                      <div className="p-8 bg-[#050505] h-[600px] overflow-y-auto font-mono text-xs text-[#CCCCCC] relative z-0">
                        <pre className="whitespace-pre-wrap">{activeFile.content}</pre>
                      </div>
                    );
                  }
                })()}
              </div>
            </section>
          )}

          {/* GitHub Integration */}
          <section className="pt-12 border-t border-[#222222]">
            <GitHubIntegration projectId={projectId} profile={profile} project={project} />
          </section>

          {/* Hero Section */}
          <section className="pt-12 border-t border-[#222222]">
            <div className="flex items-center justify-between mb-8">
              <div className="space-y-1">
                <h3 className="tech-label text-white">Project Vessel Gallery</h3>
                <p className="text-[10px] text-[#555555] font-sans uppercase tracking-widest leading-relaxed">
                  Historical deployments and published builds for this architecture.
                </p>
              </div>
              <Link 
                to="/gallery"
                className="text-[10px] font-mono uppercase tracking-widest text-[#555555] hover:text-white flex items-center gap-2 transition-colors"
                target="_blank"
              >
                View Global Gallery <ExternalLink size={12} />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {deployments.length === 0 ? (
                <div className="md:col-span-3 h-48 bg-[#0A0A0A] border border-[#222222] border-dashed rounded flex flex-col items-center justify-center gap-4 text-[#555555]">
                  <Layers size={32} />
                  <p className="text-[10px] font-mono uppercase tracking-widest">No published builds in this project</p>
                </div>
              ) : (
                deployments.map((d) => (
                  <motion.div 
                    key={d.id}
                    whileHover={{ scale: 1.02 }}
                    onClick={() => {
                        setSelectedDeployment(d);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className={`group tech-panel p-6 cursor-pointer border-zinc-800 hover:border-[#0074D9]/50 transition-all ${selectedDeployment?.id === d.id ? 'border-[#0074D9]/50 bg-[#001F3F]/10' : ''}`}
                  >
                    <div className="flex items-center justify-between mb-6">
                      <div className={`px-2 py-0.5 rounded text-[8px] font-mono uppercase tracking-widest ${d.status === 'Live' ? 'bg-[#2ECC40]/10 text-[#2ECC40]' : 'bg-[#555555]/10 text-[#555555]'}`}>
                        {d.status || 'Draft'}
                      </div>
                      <Globe size={14} className="text-[#555555] group-hover:text-white transition-colors" />
                    </div>
                    <h4 className="text-sm font-mono text-white mb-2 truncate">{d.title}</h4>
                    <p className="text-[10px] text-[#555555] line-clamp-2 leading-relaxed mb-6">
                      {d.description || 'No description available for this synthetic build.'}
                    </p>
                    <div className="text-[8px] font-mono uppercase tracking-widest text-[#333333] flex justify-between items-center group-hover:text-[#555555] transition-colors">
                      <span>{formatDate(d.deployedAt)}</span>
                      <ChevronRight size={10} />
                    </div>
                  </motion.div>
                ))
              )}
            </div>
          </section>

          <section className="flex flex-col md:flex-row items-center justify-between gap-12 pt-12 border-t border-[#222222]">
            <div className="space-y-6 max-w-xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#001F3F] border border-[#0074D9]/30 rounded-full">
                <Sparkles size={12} className="text-[#0074D9]" />
                <span className="text-[10px] font-mono uppercase tracking-widest text-[#0074D9]">Distribution Ready</span>
              </div>
              <h2 className="text-4xl font-sans font-bold tracking-tight text-white leading-tight">
                Your Launch Kit is one click away.
              </h2>
              <p className="text-[#888888] leading-relaxed">
                We take your project architecture and synthesize high-converting distribution assets for Product Hunt, X, and LinkedIn.
              </p>
              <button 
                onClick={handleGenerateLaunchKit}
                disabled={!launchPackage || isGenerating}
                className="tech-button flex items-center gap-3 px-8 py-4"
              >
                {isGenerating ? (
                  <div className="flex items-center gap-3">
                    <Loader2 className="animate-spin" size={18} />
                    <span>Generating ({generationTime}s)</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <Rocket size={18} />
                    <span>{launchKit ? 'Regenerate Launch Kit' : 'Generate Launch Kit'}</span>
                  </div>
                )}
              </button>
            </div>
            
            <div className="tech-panel w-full md:w-80 p-8 space-y-6">
              <div className="space-y-1">
                <h3 className="tech-label text-[#555555]">Source Asset</h3>
                <p className="text-sm font-medium text-white truncate">{launchPackage?.title || 'No Launch Package Found'}</p>
              </div>
              <div className="space-y-4">
                <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-widest">
                  <span className="text-[#555555]">Readiness</span>
                  <span className={launchPackage ? 'text-[#2ECC40]' : 'text-[#FF4444]'}>{launchPackage ? '100%' : '0%'}</span>
                </div>
                <div className="w-full h-1.5 bg-[#111111] rounded-full overflow-hidden">
                  <motion.div 
                    initial={{ width: 0 }}
                    animate={{ width: launchPackage ? '100%' : '0%' }}
                    className="h-full bg-[#2ECC40]"
                  />
                </div>
              </div>
              {!launchPackage && (
                <Link 
                  to={`/studio/think?project=${projectId}`}
                  className="block text-center py-3 bg-[#111111] text-[#888888] rounded text-[10px] font-mono uppercase tracking-widest hover:text-white transition-colors"
                >
                  Go to Accelerator →
                </Link>
              )}
            </div>
          </section>

          {/* Launch Kit Grid */}
          <AnimatePresence>
            {launchKit && (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="grid grid-cols-1 md:grid-cols-2 gap-6"
              >
                <div className="md:col-span-2">
                  <h3 className="tech-label text-[#555555] mb-6">Generated Distribution Assets</h3>
                </div>
                <LaunchKitCard 
                  title="Product Hunt Submission" 
                  icon={Globe} 
                  content={typeof launchKit.productHunt === 'string' 
                    ? `TITLE: ${launchKit.productHunt.split('\n')[0]}\n\n${launchKit.productHunt}`
                    : JSON.stringify(launchKit.productHunt, null, 2)
                  } 
                />
                <LaunchKitCard 
                  title="X (Twitter) Thread" 
                  icon={Share2} 
                  content={launchKit.xThread} 
                />
                <div className="md:col-span-2">
                  <LaunchKitCard 
                    title="LinkedIn Announcement" 
                    icon={ExternalLink} 
                    content={launchKit.linkedIn} 
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Webhook Section (Pro) */}
          <section className="pt-12 border-t border-[#222222]">
            <div className="tech-panel p-8 md:p-12 flex flex-col md:flex-row gap-12">
              <div className="flex-1 space-y-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#001F3F] border border-[#0074D9]/30 rounded-lg flex items-center justify-center text-[#0074D9]">
                    <Terminal size={20} />
                  </div>
                  <h3 className="text-xl font-sans font-bold tracking-tight text-white">Webhook Relay</h3>
                </div>
                <p className="text-sm text-[#888888] leading-relaxed">
                  Automatically push your launch data to Zapier, Make, or your custom backend. Perfect for triggering automated outreach or updating your CRM.
                </p>
                <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-[#555555]">
                  <ShieldCheck size={12} className="text-[#2ECC40]" />
                  <span>Encrypted End-to-End</span>
                </div>
              </div>

              <div className="w-full md:w-96 space-y-6 relative">
                {!profile?.isPro && (
                  <div className="absolute inset-0 z-10 bg-[#050505]/80 backdrop-blur-[2px] rounded flex items-center justify-center p-6 text-center border border-[#222222]">
                    <div className="space-y-4">
                      <Zap size={24} className="text-[#0074D9] mx-auto" />
                      <p className="tech-label text-white">Founder Status Required</p>
                      <Link 
                        to="/studio/checkout"
                        className="tech-button block px-6 py-2"
                      >
                        Upgrade Now
                      </Link>
                    </div>
                  </div>
                )}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <label className="tech-label text-[#555555]">Webhook URL</label>
                    <input 
                      type="url" 
                      value={webhookUrl}
                      onChange={(e) => setWebhookUrl(e.target.value)}
                      placeholder="https://hooks.zapier.com/..."
                      className="tech-input w-full"
                    />
                  </div>
                  <div className="flex gap-3">
                    <button 
                      onClick={handleSaveWebhook}
                      disabled={isSavingWebhook || !profile?.isPro}
                      className="tech-button flex-1 py-3"
                    >
                      {isSavingWebhook ? 'Saving...' : 'Save URL'}
                    </button>
                    <button 
                      onClick={handleTestWebhook}
                      disabled={isTestingWebhook || !webhookUrl || !profile?.isPro}
                      className="px-4 py-3 border border-[#222222] text-[#555555] rounded hover:text-white hover:bg-[#111111] transition-all disabled:opacity-50"
                      title="Test Webhook"
                    >
                      {isTestingWebhook ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* Ship AI Assistant (Support Chatbot) */}
      <div className="fixed bottom-8 right-8 z-50">
        <button 
          onClick={() => setShowSupportChat(!showSupportChat)}
          className="w-14 h-14 bg-[#0074D9] text-white rounded-full flex items-center justify-center shadow-[0_0_30px_rgba(0,116,217,0.3)] hover:bg-[#0056b3] transition-all group"
        >
          <Sparkles className="group-hover:scale-110 transition-transform" size={24} />
        </button>

        <AnimatePresence>
          {showSupportChat && (
            <motion.div 
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="absolute bottom-20 right-0 w-80 bg-[#0A0A0A] border border-[#222222] rounded shadow-2xl overflow-hidden flex flex-col"
            >
              <div className="p-6 bg-[#111111] border-b border-[#222222]">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-[#001F3F] border border-[#0074D9]/30 rounded-lg flex items-center justify-center text-[#0074D9]">
                    <Sparkles size={16} />
                  </div>
                  <div>
                    <h3 className="tech-label text-white">Ship Assistant</h3>
                    <p className="text-[8px] text-[#555555] uppercase tracking-widest">Support & Feedback</p>
                  </div>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <p className="text-[10px] text-[#888888] leading-relaxed">
                  Need help with your deployment or have a technical question? Send a message to our support team.
                </p>
                <textarea 
                  value={supportMessage}
                  onChange={(e) => setSupportMessage(e.target.value)}
                  placeholder="Describe your issue..."
                  className="tech-input w-full h-32 resize-none"
                />
                <button 
                  onClick={handleSendSupport}
                  disabled={isSendingSupport || !supportMessage.trim()}
                  className="tech-button w-full py-3"
                >
                  {isSendingSupport ? 'Sending...' : 'Send Ticket'}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
